import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { stripeService } from '../services/stripe';
import apiClient from '../services/api';

/**
 * AuthCallback Component
 * 
 * Handles the OAuth callback from Emergent Auth.
 * Processes the session_id from URL fragment, exchanges it for user data,
 * and redirects to dashboard.
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing (React StrictMode)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processOAuthCallback = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        if (!hash || !hash.includes('session_id=')) {
          console.error('No session_id found in URL');
          navigate('/login', { replace: true });
          return;
        }

        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('session_id is empty');
          navigate('/login', { replace: true });
          return;
        }


        // Exchange session_id with backend
        const response = await apiClient.post('/api/auth/google/session', {
          session_id: sessionId
        });

        if (response.data.success) {
          // Store session token
          const { session_token, member } = response.data;
          sessionStorage.setItem('session_token', session_token);
          sessionStorage.setItem('user_email', member.email);
          sessionStorage.setItem('user_name', member.name);
          await stripeService.claimPendingCheckout(); // link a paid public-signup checkout before leaving


          // Navigate to dashboard with user data
          navigate('/dashboard', { 
            replace: true,
            state: { user: member }
          });
        } else {
          throw new Error('OAuth session exchange failed');
        }

      } catch (error) {
        console.error('OAuth callback error:', error);
        navigate('/login', { 
          replace: true,
          state: { error: 'Authentication failed. Please try again.' }
        });
      }
    };

    processOAuthCallback();
  }, [location, navigate]);

  // Show loading while processing
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
      <div className="text-center">
        <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="text-white text-lg font-medium">Signing you in...</p>
        <p className="text-[#9CB0C7] text-sm mt-2">Please wait while we complete your authentication</p>
      </div>
    </div>
  );
}
